import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { backendService } from "../../../../services/backend.service";
import type { HiringMetricKey, HiringRecurringGapsResponse } from "../types";
import type { HireShiftTypes } from "./useSuggestedHireEdits";

export const HIRING_QUERY_KEY = "hiring-recurring-gaps";

type Args = {
	census: number | undefined;
	jobId: string | undefined;
	// Active metric levers (OT/Agency/Pickup/Absences). They change the toggle-adjusted "Missing" the
	// recommendations are built from, so toggling refetches. Sorted into the key for a stable hash.
	includedMetrics: HiringMetricKey[];
	// Suggested-hire generation shape, only sent once the user has picked one in Settings — omitting it
	// lets the backend derive both from the selected job, which is a different (and better) default than
	// any value we could invent here. A change refetches: the server regenerates the suggestions.
	shiftsPerWeek: number | undefined;
	shiftLength: number | undefined;
	/**
	 * Whether ONE suggested person may cover more than one shift type. `undefined` until the user picks,
	 * so the backend applies its own default — which is the same "one" this module shows.
	 */
	shiftTypes: HireShiftTypes | undefined;
	// The Hire tab's shift chips. `undefined` means no filter, so the backend packs every template; a
	// selection scopes GENERATION, not just what renders — the gap on a switched-off shift is dropped and
	// the people who were covering it are re-packed onto the shifts still in scope. Sorted into the key so
	// picking the same two chips in a different order is one cache entry, not two.
	hireShiftLabels: string[] | undefined;
};

export function useHiringPredictionData({ census, jobId, includedMetrics, shiftsPerWeek, shiftLength, shiftTypes, hireShiftLabels }: Args) {
	const { chosenDepartment } = useHospitalData();
	const { trackError } = useAnalytics();

	const sortedMetrics = [...includedMetrics].sort();
	const sortedHireShifts = hireShiftLabels === undefined ? undefined : [...hireShiftLabels].sort();

	return useQuery<HiringRecurringGapsResponse>({
		queryKey: [
			HIRING_QUERY_KEY,
			chosenDepartment.department_id,
			census,
			jobId,
			sortedMetrics.join(","),
			shiftsPerWeek,
			shiftLength,
			// In the key because it regenerates the suggestions — leave it out and changing the mode
			// silently serves the previous mode's cached answer.
			shiftTypes,
			sortedHireShifts?.join(","),
		],
		queryFn: async (): Promise<HiringRecurringGapsResponse> => {
			try {
				const { data } = await backendService.getHiringRecurringGaps(chosenDepartment.department_id, {
					census,
					jobId,
					includedMetrics: sortedMetrics,
					shiftsPerWeek,
					shiftLength,
					shiftTypes,
					hireShiftLabels: sortedHireShifts,
				});
				return data;
			} catch (err) {
				trackError("hiring_prediction_fetch_error", { department_id: chosenDepartment.department_id });
				throw err;
			}
		},
		enabled: Boolean(chosenDepartment.department_id) && chosenDepartment.master_schedule_weeks > 0,
		// Keep the prior grids/recommendations on screen while a toggle-driven refetch is in flight.
		placeholderData: keepPreviousData,
	});
}
